import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { easeOut } from "../../motion/transitions";

/**
 * Left slide-over panel. Soft backdrop keeps the letter readable for live preview.
 */
function SideDrawer({ open, onClose, title, children, footer }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            key="side-drawer-backdrop"
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[1000] bg-black/30 lg:hidden"
            onClick={onClose}
            aria-label="Fermer"
          />
          <motion.aside
            key="side-drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            variants={{
              hidden: { x: "-100%" },
              visible: {
                x: 0,
                transition: { duration: 0.3, ease: easeOut },
              },
              exit: {
                x: "-100%",
                transition: { duration: 0.25, ease: easeOut },
              },
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-y-0 left-0 z-[1001] flex w-[min(300px,88vw)] flex-col bg-[#F7F3F2] shadow-[4px_0_24px_rgba(0,0,0,0.1)] lg:hidden"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-greyLight/40 px-4 py-4">
              <h2 className="font-josefin text-sm font-bold uppercase tracking-wider text-modalTitle">
                {title}
              </h2>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-md text-secondaryText transition hover:bg-black/[0.04]"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-scroll min-h-0 flex-1 px-4 py-5">
              <div className="modal-scroll-content">{children}</div>
            </div>

            {footer && (
              <div className="shrink-0 border-t border-greyLight/40 bg-[#F7F3F2] px-4 py-4">
                {footer}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default SideDrawer;
